import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Grid3x3, Loader2, MapPin, RefreshCw } from 'lucide-react'
import { ApiError, fetchStores, type StoreOption } from '../api/auth'
import { fetchGridView, type GridView } from '../api/grid'
import { DashboardGroupSubMenu } from '../components/DashboardGroupSubMenu'
import {
  GridStaircaseChart,
  GRID_PERIODS,
  type GridPeriod,
} from '../components/GridStaircaseChart'
import { ShopPicker } from '../components/ShopPicker'
import { useAuth } from '../context/AuthContext'
import { normalizeStoreCode } from '../dashboard/embeds'
import {
  defaultShopId,
  insightShopsForUser,
  needsShopList,
  shopLabel,
  type InsightShop,
} from '../dashboard/insightShops'
import { formatCurrency, formatNumber } from '../lib/format'
import { navigate, useRoute } from '../lib/router'

const PERIOD_BUTTON =
  'rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors'
const PERIOD_ACTIVE =
  'border-salsa-500 bg-salsa-500/10 text-salsa-700 dark:border-salsa-500/50 dark:text-salsa-300'
const PERIOD_IDLE =
  'border-slate-200 text-slate-600 hover:border-salsa-300 hover:text-salsa-700 dark:border-white/10 dark:text-slate-300 dark:hover:border-salsa-500/40 dark:hover:text-salsa-200'

function InfoCard({
  label,
  value,
  hint,
}: {
  label: string
  value: string
  hint: string
}) {
  return (
    <article className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.03]">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
        {label}
      </p>
      <p className="mt-1 text-sm font-semibold tabular-nums text-slate-900 dark:text-white">
        {value}
      </p>
      <p className="mt-0.5 text-[10px] text-slate-400 dark:text-slate-500">{hint}</p>
    </article>
  )
}

/**
 * Grid Viewer — the actual COL grid, one shop at a time.
 *
 * The period switch changes the unit only (hours/day, hours/week, hours/year);
 * it does not re-run a sales lookup because the viewer is showing the grid
 * blueprint itself. The shop selector is a radio group: every shop is available,
 * exactly one is displayed.
 */
export function GridViewerPage() {
  const { user } = useAuth()
  const route = useRoute()
  const wantsShopList = needsShopList(user)

  const [catalogue, setCatalogue] = useState<StoreOption[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [period, setPeriod] = useState<GridPeriod>('day')

  const scopeKey = user
    ? `${user.id}:${user.role}:${user.store_id ?? ''}:${(user.scope_store_ids ?? []).join(',')}`
    : 'anonymous'

  useEffect(() => {
    if (!wantsShopList) return
    let cancelled = false
    fetchStores()
      .then((list) => {
        if (cancelled) return
        setCatalogue(list)
        setLoadError(null)
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError('Could not load the shop list — using the shops in your scope.')
        }
      })
    return () => {
      cancelled = true
    }
  }, [wantsShopList, scopeKey])

  const shops = useMemo(() => insightShopsForUser(user, catalogue), [user, catalogue])
  const wantedCode = normalizeStoreCode(route.query.get('shop'))

  const activeShop = useMemo<InsightShop | null>(() => {
    const chosen =
      (wantedCode ? shops.find((shop) => shop.code === wantedCode) : undefined) ??
      shops.find((shop) => shop.id === selectedId) ??
      shops.find((shop) => shop.id === defaultShopId(user, shops)) ??
      shops[0]
    return chosen ?? null
  }, [shops, selectedId, wantedCode, user])

  const selectShop = useCallback(
    (id: number) => {
      setSelectedId(id)
      const shop = shops.find((candidate) => candidate.id === id)
      if (!shop) return
      const params = new URLSearchParams(route.query)
      params.set('shop', shop.code)
      navigate(`/insights/grid-viewer?${params.toString()}`)
    },
    [shops, route.query],
  )

  const gridQuery = useQuery({
    queryKey: ['grid-viewer', activeShop?.id ?? 'none'],
    queryFn: ({ signal }) => fetchGridView({ storeId: activeShop!.id, signal }),
    enabled: Boolean(user && activeShop),
    staleTime: 5 * 60_000,
    retry: 1,
  })

  const data = gridQuery.data
  const shopName = shopLabel(activeShop)
  const errorMessage =
    gridQuery.error instanceof ApiError
      ? gridQuery.error.message
      : gridQuery.error
        ? 'The grid is unavailable right now.'
        : null

  return (
    <div id="grid-viewer-panel" className="space-y-4">
      <DashboardGroupSubMenu
        activeKey="grid_viewer"
        fallbackGroupKey="insights"
        panelId="grid-viewer-panel"
      />

      <section className="card space-y-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-salsa-500 to-mango-500 text-white shadow-sm"
              aria-hidden="true"
            >
              <Grid3x3 className="h-4 w-4" />
            </span>
            <div>
              <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
                Grid Viewer
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                The actual COL staircase for one shop — hours per day, week or year.
              </p>
            </div>
          </div>
          <span className="badge bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
            COL workbook
          </span>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-3">
          {shops.length > 1 ? (
            <div className="min-w-0 flex-1">
              <ShopPicker
                stores={shops}
                selectedId={activeShop?.id ?? null}
                onSelect={selectShop}
                hint={`${shops.length} shops in your scope · one grid at a time`}
              />
            </div>
          ) : activeShop ? (
            <p className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="font-semibold text-slate-700 dark:text-slate-200">
                {shopName}
              </span>
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Period
            </span>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Grid period">
              {GRID_PERIODS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setPeriod(option.key)}
                  aria-pressed={period === option.key}
                  title={option.unit}
                  className={`${PERIOD_BUTTON} ${
                    period === option.key ? PERIOD_ACTIVE : PERIOD_IDLE
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loadError && (
          <p className="flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" /> {loadError}
          </p>
        )}
      </section>

      {!activeShop ? (
        <section className="card p-4 text-sm text-slate-500 dark:text-slate-400">
          No shops are available in your scope.
        </section>
      ) : gridQuery.isPending ? (
        <section className="card flex items-center gap-2 p-4 text-sm text-slate-500 dark:text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading the {shopName} grid…
        </section>
      ) : gridQuery.isError ? (
        <section className="card space-y-2 p-4">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-rose-700 dark:text-rose-300">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            Could not load the grid for {shopName}
          </p>
          {errorMessage && (
            <p className="text-xs text-slate-500 dark:text-slate-400">{errorMessage}</p>
          )}
          <button
            type="button"
            onClick={() => void gridQuery.refetch()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 transition-colors hover:border-salsa-300 hover:text-salsa-700 dark:border-white/10 dark:text-slate-300 dark:hover:border-salsa-500/40 dark:hover:text-salsa-200"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Retry
          </button>
        </section>
      ) : data ? (
        <GridViewContent data={data} period={period} />
      ) : null}
    </div>
  )
}

export function GridViewContent({
  data,
  period,
}: {
  data: GridView
  period: GridPeriod
}) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <InfoCard
          label="Blueprint"
          value={data.grid_label}
          hint={`${data.scenarios.length} staircase steps`}
        />
        <InfoCard
          label="AOV"
          value={formatCurrency(data.aov_eur)}
          hint="Used for revenue per day"
        />
        <InfoCard
          label="Transactions / day"
          value={`${formatNumber(data.minimum_transactions_per_day, 0)} – ${formatNumber(
            data.maximum_transactions_per_day,
            0,
          )}`}
          hint="Grid scenario range"
        />
        <InfoCard
          label="Daily sales"
          value={`${formatCurrency(data.minimum_daily_sales_eur)} – ${formatCurrency(
            data.maximum_daily_sales_eur,
          )}`}
          hint="Transactions × store AOV"
        />
      </div>

      <section className="card space-y-3 p-4">
        <header className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
              {data.store_code} · {data.store_name}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {data.grid_label} · {formatNumber(data.scenarios.length, 0)} steps ·{' '}
              {GRID_PERIODS.find((option) => option.key === period)?.unit}
            </p>
          </div>
          <span className="badge bg-slate-500/15 text-slate-600 dark:text-slate-300">
            {period === 'day' ? 'Day view' : period === 'week' ? 'Week view' : 'Year view'}
          </span>
        </header>

        <GridStaircaseChart
          scenarios={data.scenarios}
          period={period}
          shopName={`${data.store_code} · ${data.store_name}`}
        />
      </section>

      <p className="px-1 text-[10px] leading-relaxed text-slate-400 dark:text-slate-500">
        Source: COL Scenarios (Labor Grid) · {data.grid_label} · revenue/day = transactions ×
        AOV. Week = 7 days; year = 52 weeks. The grid is a step lookup: it never interpolates
        between scenarios.
      </p>
    </>
  )
}
